/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSpeedDialsColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining columns for the Speed Dials data grid.
────────────────────────────────────────────────────────────────*/

import React, { useMemo } from 'react';
import { Phone } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { DataGridColumn } from '../../../hooks/useDataGrid';
import type { SpeedDial } from '../api/speedDialsApi';

export function useSpeedDialsColumns(renderActions: (item: SpeedDial) => React.ReactNode) {
  const { t, refreshKey, i18n } = useI18n();

  const columns = useMemo<DataGridColumn<SpeedDial>[]>(
    () => [
      {
        key: 'speedCode',
        label: t('speedDials.columns.speedCode'),
        visible: true,
        sortable: true,
        render: (value: unknown, item: SpeedDial) => (
          <span className="font-mono font-medium text-primary">{item.speedCode || ''}</span>
        )
      },
      {
        key: 'destination',
        label: t('speedDials.columns.destination'),
        visible: true,
        sortable: true,
        render: (value: unknown, item: SpeedDial) => (
          <span className="font-mono text-sm">{item.destination || ''}</span>
        )
      },
      {
        key: 'description',
        label: t('speedDials.columns.description'),
        visible: true,
        sortable: true,
        render: (value: unknown, item: SpeedDial) => (
          <span className="text-sm text-muted-foreground">{item.description || '-'}</span>
        )
      },
      {
        key: 'classOfService',
        label: t('speedDials.columns.classOfService'),
        visible: true,
        sortable: false,
        render: (value: unknown, item: SpeedDial) => (
          <span className="text-sm">{item.classOfService?.name || '-'}</span>
        )
      },
      {
        key: 'enabled',
        label: t('speedDials.columns.enabled'),
        visible: true,
        sortable: true,
        render: (value: boolean) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
              value
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
          >
            {value ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions_title'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: SpeedDial) => renderActions(item)
      }
    ],
    [t, refreshKey, i18n.language, renderActions]
  );

  return { columns };
}

